import React, { Component } from "react";
import { connect } from "react-redux";
import { injectIntl, FormattedMessage } from "react-intl";
import { Icon, Button, Tooltip } from "antd";

import DefaultUserImage from "../../assets/img/avatar_user_black.png";
import { ReactComponent as AtSvg } from "../../assets/icon/ic_at_24dp.svg";

// Constants
import keyCode from "../../constants/keyCode";

// Utils
import { debounce } from "../../utils/debounce";
import { isTruthy } from "../../utils/util";
import { formatFileSize } from "../../utils/file.util";

// Services
import UserService from "../../services/user";
import ChatService from "../../services/chat";

// Store
import { updateChat } from "../../store/actions/chats";

// Components
import FlagSelector from "../FlagSelector";
import Mention from "../Mention";

import "./MessageInput.scss";

const DEFAULT_ERROR_MESSAGE_DELAY = 6000;

function isAcceptedFileType(file, allowedTypes = []) {
  if (!allowedTypes || allowedTypes.length === 0) {
    return true;
  }

  const fileExtension = file.name.split(".").pop().toLowerCase();

  return allowedTypes.some(
    (accepted) => fileExtension === accepted.trim().toLowerCase()
  );
}

class MessageInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      value: "",
      valueHtml: "",
      users: [],
      loadingUsers: false,
      sendingMessage: false,
      uploadingFile: false,
      fileUploadError: null,
      fileUploadTooltipVisible: false,
      allInactiveUsers: false,
      searchValue: "",
      suggestionsLength: 0,
    };

    this.onKeyDown = this.onKeyDown.bind(this);
    this.send = this.send.bind(this);
    this.triggerFilePicker = this.triggerFilePicker.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);

    this.fileUploadErrorTimeout = null;
  }

  componentDidMount() {
    const { initialState, chat, updateChat } = this.props;

    this.mounted = true;

    const localInitialState = initialState || "";

    this.setState(
      {
        value: localInitialState,
      },
      () => this.debounceChange(localInitialState)
    );

    if (chat.focusOnOpen) {
      this.mention.setFocus();

      updateChat({
        id: chat.id,
        focusOnOpen: false,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.chatState === "" &&
      this.props.chatState !== prevProps.chatState &&
      this.mounted
    ) {
      this.setState(
        {
          value: "",
        },
        () => this.mention && this.mention.clear && this.mention.clear()
      );
    }

    if (
      this.props.waitingMessageSentConfirmation === false &&
      prevProps.waitingMessageSentConfirmation === true
    ) {
      this.mention.node.contentEditable = true;
    }

    // verifica usuários
    const allInactiveUsers = this.props.chat?.recipients?.every(
      (recipient) => !recipient.isActive
    );

    const prevState = { ...this.state };

    if (allInactiveUsers !== prevState.allInactiveUsers) {
      this.setState({
        allInactiveUsers,
      });
    }

    const { draft, profiles } = this.props;
    const defaultCategory = window.Social.getChatConfig().defaultCategory;

    if (
      prevProps.draft.category !== defaultCategory &&
      draft.category === defaultCategory
    ) {
      const normalizeValue = this.state.value
        .replace(/@(manager|last-approver)/g, "")
        .trim();

      this.setState({ value: normalizeValue }, () => {
        if (!this.mention?.node) {
          return;
        }

        profiles.forEach((profile) => {
          const element = document.querySelector(
            `[data-user="${profile}"]`
          )?.outerHTML;

          if (element) {
            this.mention.node.innerHTML = this.mention.node.innerHTML.replace(
              element,
              ""
            );
          }
        });
      });

      this.debounceChange(normalizeValue);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.debounceChange.clear();
    this.debounceUserSearchChange.clear();

    if (this.fileUploadErrorTimeout) {
      clearTimeout(this.fileUploadErrorTimeout);
    }
  }

  fetchUsers(params = {}, callback) {
    UserService.getUsers(params).then(callback);
  }

  isValidInput(value) {
    return value.trim().length > 0;
  }

  onChange(html, text) {
    if (this.mounted) {
      const chatConfig = window.Social.getChatConfig();

      if (text?.length < chatConfig?.messageMaxLength) {
        this.setState({
          value: text,
          valueHtml: html,
        });
      }

      this.debounceChange(html);
    }
  }

  debounceChange = debounce((value) => {
    if (this.mounted) {
      this.props.onChange && this.props.onChange(value);
    }
  }, 250);

  getUserAvatar(user) {
    return DefaultUserImage;
  }

  debounceUserSearchChange = debounce((searchValue, callback) => {
    const { chat, newChat, currentUser } = this.props;
    const { formatMessage } = this.props.intl;
    const { category } = this.props.draft;

    const chatConfig = window.Social.getChatConfig();
    const defaultCategory = window.Social.getChatConfig().defaultCategory;
    const paths = chatConfig.chatRecipientsUsernamePath;
    const privacyConfig = Object.values(window.Social.getPrivacyTypesConfig());

    const currentChatHashElement = chat?.elements?.find(
      (elem) => elem.description === chatConfig.hashElementPrefix
    );

    const currentChatPrivacyElement = chat?.elements?.find(
      (elem) => elem.description === chatConfig.privacyElement
    );

    const currentChatPrivacy = currentChatPrivacyElement
      ? privacyConfig.find(
          (elem) => elem.value === currentChatPrivacyElement.content
        )
      : privacyConfig.find((elem) => elem.isDefault);

    let profiles = [];

    if (currentChatHashElement || (category !== defaultCategory && newChat)) {
      profiles = this.props.profiles.map((elem) => {
        return {
          key: elem,
          value: formatMessage({ id: `profiles.${elem}` }),
          usernameAlias: formatMessage({ id: `profiles.${elem}` }),
          isActive: true,
        };
      });

      profiles = profiles.filter((elem) => {
        return (
          elem.value?.toLowerCase().includes(searchValue.toLowerCase()) ||
          elem.key.toLowerCase().includes(searchValue.toLowerCase())
        );
      });
    }

    if (currentChatPrivacy?.onlyCurrentUsersOnMention && !chat.newChat) {
      this.setState({
        loadingUsers: true,
      });

      const currentUserLogin = currentUser?.login;

      let recipients = chat.recipients.filter((elem) => {
        let username = elem;
        for (let i = 0, pathsLength = paths.length; i < pathsLength; i++) {
          username = username[paths[i]];
        }

        if (username === currentUserLogin) {
          return false;
        }

        return username?.toLowerCase().includes(searchValue.toLowerCase());
      });

      let suggestions = recipients?.map((recipient) => {
        let suggestion = recipient;

        for (let i = 0; i < paths.length; i++) {
          suggestion = suggestion[paths[i]];
        }

        return {
          key: suggestion,
          value: suggestion,
          isActive: recipient.isActive,
        };
      });

      suggestions = [].concat(profiles, suggestions);

      this.setState({
        searchValue,
        loadingUsers: false,
      });

      return callback(suggestions);
    }

    if (searchValue.length > 0) {
      this.setState({
        loadingUsers: true,
      });

      const params = {
        name: searchValue,
        enabled: true,
      };

      this.fetchUsers(params, (res) => {
        const currentUserLogin = this.props.currentUser?.login;

        let suggestions = (res?.data?.content || [])
          .filter((suggestion) => suggestion.username !== currentUserLogin)
          .map((suggestion) => {
            return {
              key: suggestion.username,
              value: suggestion.name,
              isActive: true,
            };
          });

        suggestions = [].concat(profiles, suggestions);

        this.setState({
          searchValue,
          loadingUsers: false,
        });

        return callback(suggestions);
      });
    }
  }, 500);

  onKeyDown(e) {
    const { pressEnterToSendMessage } = this.props.settings;

    const pressedEnter = e.keyCode === keyCode.ENTER;
    const shiftKeyIsPressed = e.shiftKey;

    const userHitEnterToSubmit = [
      pressEnterToSendMessage,
      this.canSendMessage(),
      pressedEnter,
      !shiftKeyIsPressed,
    ].every(isTruthy);

    if (userHitEnterToSubmit && !this.state.allInactiveUsers) {
      this.send();
    }
  }

  mapUsers() {
    return this.state.users.map((elem) => elem.username);
  }

  clearFileUploadError() {
    if (!this.mounted) {
      return;
    }

    if (this.fileUploadErrorTimeout) {
      clearTimeout(this.fileUploadErrorTimeout);
      this.fileUploadErrorTimeout = null;
    }

    this.setState(
      {
        fileUploadTooltipVisible: false,
      },
      () => {
        this.setState({
          fileUploadError: null,
        });
      }
    );
  }

  scheduleFileUploadErrorClear() {
    this.fileUploadErrorTimeout = setTimeout(
      () => this.clearFileUploadError(),
      DEFAULT_ERROR_MESSAGE_DELAY
    );
  }

  getFileUploadErrorTitle() {
    const { intl, templateSettings } = this.props;
    const { fileUploadError } = this.state;

    switch (fileUploadError) {
      case "uploadError":
        return intl.formatMessage({ id: "chat.attachmentError" });

      case "fileTypeError":
        return intl.formatMessage({ id: "chat.fileTypeError" });

      case "fileEmptyError":
        return intl.formatMessage({ id: "chat.fileEmptyError" });

      case "fileSizeError": {
        const maxSizeKB = templateSettings?.maxFileSizeAllowed || 0;
        const maxSizeLabel = formatFileSize(maxSizeKB);

        return intl.formatMessage(
          {
            id: "chat.fileSizeExceeded",
          },
          {
            maxSize: maxSizeLabel,
          }
        );
      }

      default:
        return "";
    }
  }

  getFileUploadTooltipTitle() {
    const { intl } = this.props;
    const { fileUploadError } = this.state;

    if (this.props.newChat) {
      return intl.formatMessage({ id: "chat.newChatFileDisabled" });
    }

    if (fileUploadError) {
      return this.getFileUploadErrorTitle();
    }

    return intl.formatMessage({ id: "chat.attachFile" });
  }

  handleFileUpload(e) {
    const file = e?.target?.files?.[0];
    const chatId = this.props.chat?.id;
    const { allowedTypes, maxFileSizeAllowed } = this.props.templateSettings;

    e.target.value = "";

    if (!file || !chatId) {
      return;
    }

    if (!isAcceptedFileType(file, allowedTypes)) {
      this.setState({
        fileUploadError: "fileTypeError",
        fileUploadTooltipVisible: true,
      });

      this.scheduleFileUploadErrorClear();

      return;
    }

    if (file.size === 0) {
      this.setState({
        fileUploadError: "fileEmptyError",
        fileUploadTooltipVisible: true,
      });

      this.scheduleFileUploadErrorClear();

      return;
    }

    const maxFileSizeBytes = (maxFileSizeAllowed || 0) * 1024;

    if (file.size > maxFileSizeBytes) {
      this.setState({
        fileUploadError: "fileSizeError",
        fileUploadTooltipVisible: true,
      });

      this.scheduleFileUploadErrorClear();

      return;
    }

    const pendingMessage = this.isValidInput(this.state.value)
      ? {
          value: this.state.value,
          mentions: this.mention.getMentions(),
        }
      : null;

    this.setState({ uploadingFile: true }, async () => {
      try {
        await ChatService.uploadAttachment(chatId, file);

        if (this.mounted && pendingMessage) {
          this.props.sendMessage(pendingMessage.value, pendingMessage.mentions);
        }
      } catch {
        if (this.mounted) {
          this.setState({
            fileUploadError: "uploadError",
            fileUploadTooltipVisible: true,
          });

          this.scheduleFileUploadErrorClear();
        }
      } finally {
        if (this.mounted) {
          this.setState({ uploadingFile: false });
        }
      }
    });
  }

  triggerFilePicker() {
    const canOpenFilePicker =
      !this.state.uploadingFile &&
      !this.props.waitingMessageSentConfirmation &&
      this.fileInputRef;

    if (!canOpenFilePicker) {
      return;
    }

    this.clearFileUploadError();

    this.fileInputRef.click();
  }

  send() {
    if (this.canSendMessage() && this.isValidInput(this.state.value)) {
      this.mention.node.contentEditable = false;

      this.setState(
        {
          sendingMessage: true,
        },
        () => {
          setTimeout(() => {
            this.setState(
              {
                sendingMessage: false,
              },
              () =>
                this.props.sendMessage(
                  this.state.value,
                  this.mention.getMentions()
                )
            );
          }, 300);
        }
      );
    }
  }

  removeReactivity(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  getMentionsOptions() {
    const { profiles } = this.props;
    const { formatMessage } = this.props.intl;

    return {
      collection: [
        {
          trigger: "@",
          lookup: (item) => `${item.key} ${item.value}`,
          fillAttr: "key",
          requireLeadingSpace: true,

          values: (text, callback) =>
            this.debounceUserSearchChange(text, (suggestions) => {
              const activeUsers = suggestions.filter((user) => user.isActive);

              this.setState({
                suggestionsLength: activeUsers.length,
              });

              callback(activeUsers);
            }),

          // Devemos manter o template sem espaçamento ou quebra de linha por conta da lib
          selectTemplate: (item) => {
            const key = item.original.key;
            const displayName = profiles.includes(key)
              ? formatMessage({ id: `profiles.${key}` })
              : key;

            return `<span contenteditable="false" style="color: #1976D2" class="mention-user" data-user="${key}">@${displayName}</span>`;
          },

          menuItemTemplate: (item) => {
            const avatarSrc = this.getUserAvatar(item.original);

            const inactiveTemplate = !item.original.isActive
              ? `<div class="user-inactive">${this.props.intl.messages["chat.disabledUser"]}</div>`
              : "";

            const classes = item.original.isActive
              ? "user-suggestion-item list-item"
              : "user-suggestion-item list-item disabled";

            return `
              <div class="${classes}">
                <div class="image">
                  <img src="${avatarSrc}"/>
                </div>

                <div class="content">
                  <div class="title">
                    @${item.original.usernameAlias || item.original.key}
                  </div>

                  ${inactiveTemplate}
                </div>
              </div>
            `;
          },
        },
      ],
    };
  }

  onAtClick() {
    if (this.state.value && this.state.value.length)
      this.mention.insertTextAtEnd(`@`, false);
    else this.mention.insertTextAtEnd(`&nbsp;@`, false);
  }

  onHashClick() {
    if (this.state.value && this.state.value.length)
      this.mention.insertTextAtEnd(`#`, false);
    else this.mention.insertTextAtEnd(`&nbsp;#`, false);
  }

  canSendMessage() {
    if (this.state.allInactiveUsers) {
      return false;
    }

    if (!this.props.canSendMessage) {
      return false;
    }

    if (this.state.sendingMessage) {
      return false;
    }

    if (this.state.uploadingFile) {
      return false;
    }

    if (this.isValidInput(this.state.value) && this.props.canSendMessage) {
      return true;
    }

    return false;
  }

  canSendFile() {
    return (
      !this.props.newChat &&
      !this.state.uploadingFile &&
      !this.props.waitingMessageSentConfirmation &&
      !this.state.allInactiveUsers &&
      !this.state.sendingMessage &&
      this.props.canSendMessage
    );
  }

  render() {
    const chatConfig = window.Social.getChatConfig();
    const { allowedTypes } = this.props.templateSettings;

    const MentionContainer = (
      <div
        ref={(node) => (this.mentionContainerRef = node)}
        className="message-input"
        key="message-input"
        onKeyDown={this.onKeyDown}
        onClick={this.props.onFocus}
      >
        <Mention
          className="multiple"
          ref={(node) => (this.mention = node)}
          options={this.getMentionsOptions()}
          inputType={`multiple ${this.props.newChat ? "full" : ""}`}
          placeholder={this.props.intl.messages["chat.typeMessage"]}
          dropdownClass="top"
          defaultValue={this.props.initialState}
          suggestionsLength={this.state.suggestionsLength}
          readOnly={this.props.readOnly}
          onInputChange={this.onChange.bind(this)}
        />

        <div
          className="dropdown-container"
          ref={(node) => (this.dropdownContainerRef = node)}
        />
      </div>
    );

    const MessageInputButtons = (
      <div className="message-input-buttons" key="message-input-buttons">
        <div className="action-buttons">
          <Tooltip
            placement="top"
            overlayClassName="social-tooltip"
            title={this.props.intl.messages["chat.mentionUser"]}
          >
            <Button
              type="link"
              shape="circle"
              className="action-button"
              onClick={this.onAtClick.bind(this)}
            >
              <Icon component={AtSvg} />
            </Button>
          </Tooltip>

          <div className="file-picker-container">
            <input
              ref={(node) => (this.fileInputRef = node)}
              type="file"
              accept={
                allowedTypes?.length
                  ? allowedTypes.map((t) => `.${t}`).join(",")
                  : undefined
              }
              onChange={this.handleFileUpload}
            />

            <Tooltip
              placement="top"
              title={this.getFileUploadTooltipTitle()}
              visible={this.state.fileUploadTooltipVisible}
              overlayClassName={`social-tooltip file-upload-tooltip ${
                this.state.fileUploadError ? "error" : ""
              }`}
              onVisibleChange={(visible) => {
                if (this.state.fileUploadError) {
                  return;
                }

                this.setState({ fileUploadTooltipVisible: visible });
              }}
            >
              <Button
                type="link"
                shape="circle"
                icon="paper-clip"
                className="action-button"
                disabled={this.props.newChat || !this.canSendFile()}
                onClick={this.triggerFilePicker}
              />
            </Tooltip>
          </div>
        </div>

        {chatConfig.allowFlags && this.props.newChat && (
          <FlagSelector
            flags={this.props.flags}
            onFlagSelect={this.props.onFlagSelect}
          />
        )}

        <Button
          className={`send-message ${
            chatConfig.allowFlags && this.props.newChat
              ? ""
              : "margin-left-auto"
          }`}
          type="primary"
          loading={
            this.props.waitingMessageSentConfirmation ||
            this.state.uploadingFile
          }
          disabled={!this.canSendMessage()}
          onClick={this.send}
        >
          <FormattedMessage id="general.send" />
        </Button>
      </div>
    );

    return [MentionContainer, MessageInputButtons];
  }
}

const mapStateToProps = (state) => {
  return {
    settings: state.GeneralSettings,
    currentUser: state.Users.currentUser,
    allUsersInactive: state.Users.allUsersInactive,
  };
};

const mapDispatchToProps = (dispatch) => ({
  updateChat: (chat) => dispatch(updateChat(chat)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(MessageInput));
